import React, { useMemo } from "react";
import { TextField } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";

const getUniqueCities = (addresses) => {
  const seen = new Set();
  return addresses.reduce((cities, address) => {
    const city = address?.cityName;
    if (!city || seen.has(city)) return cities;
    seen.add(city);
    cities.push(city);
    return cities;
  }, []);
};

const getStreetsForCity = (addresses, city) => {
  const streets = addresses
    .filter((address) => address?.cityName === city)
    .map((address) => address?.streetName)
    .filter(Boolean);
  const hasAnotherStreet = streets.some((street) => street !== city);
  if (!hasAnotherStreet) return streets.length ? [streets[0]] : [];
  return streets.filter((street) => street !== city);
};

/**
 * Haklai-only copy of the Classic Form 2990 city/street interaction.
 * A street whose name is the selected city is hidden when that city has any
 * other street. A settlement whose only street is its own name keeps that
 * option, because the street field is required.
 */
const ClassicAddressAutocomplete = ({
  addresses = [],
  city,
  disabled,
  error,
  helperText,
  id,
  label,
  name,
  onChange,
  textFieldWidth = 190,
  value
}) => {
  const options = useMemo(
    () => (
      name === "assetCity"
        ? getUniqueCities(addresses)
        : getStreetsForCity(addresses, city)
    ),
    [addresses, city, name]
  );
  const normalizedValue = value == null ? "" : String(value);
  const selectedValue = options.includes(normalizedValue) ? normalizedValue : null;
  const hasHelperText = helperText !== undefined && helperText !== null && helperText !== "";
  const emitChange = (nextValue) => onChange({
    target: {
      name,
      value: nextValue == null ? "" : String(nextValue)
    }
  });

  return (
    <Autocomplete
      blurOnSelect
      disabled={disabled}
      freeSolo
      fullWidth
      id={id}
      inputValue={normalizedValue}
      onInputChange={(event, nextValue, reason) => {
        if (reason === "input" || reason === "clear") emitChange(nextValue);
      }}
      onChange={(event, nextValue) => emitChange(nextValue)}
      openOnFocus
      options={options}
      value={selectedValue}
      style={{ width: `${textFieldWidth}px` }}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={hasHelperText ? helperText : undefined}
          inputProps={{
            ...params.inputProps,
            "aria-label": label
          }}
          size="small"
          variant="outlined"
        />
      )}
    />
  );
};

export default ClassicAddressAutocomplete;
